import React, { useState, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import html2pdf from 'html2pdf.js';
import {
  Plus, X, Printer, RotateCcw, ArrowLeftRight, MapPin, Navigation,
  DollarSign, Copy, CheckCircle, Lock, User, Building2,
} from 'lucide-react';
import { parseApiError } from '../utils/apiFetch';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { isPaidUser } from '../types/auth';
import BackToTools from '../components/BackToTools';

/* ── Fix Leaflet marker icons ────────────────────────────────────────── */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

/* ── Types ───────────────────────────────────────────────────────────── */
interface AccessorialRow { id: string; amount: string; name: string; }
interface QuoteTab {
  id: string; label: string;
  ratePerMile: string; fuelSurcharge: string; ratePerStop: string;
  ratePerLbs: string; ftlLtl: string; accessorials: AccessorialRow[]; margin: string;
}
interface RouteInfo {
  distanceMiles: number; distanceKm: number; durationMin: number;
  numStops: number; polyline: [number, number][]; markers: [number, number][];
}
interface PartyInfo { company: string; contact: string; address: string; city: string; state: string; phone: string; email: string; }

/* ── Helpers ─────────────────────────────────────────────────────────── */
let _qid = 1;
const newId  = () => String(_qid++);
const blankQ = (): QuoteTab => ({
  id: newId(), label: `Quote ${_qid - 1}`,
  ratePerMile: '', fuelSurcharge: '', ratePerStop: '',
  ratePerLbs: '', ftlLtl: '100', accessorials: [], margin: '',
});
const blankParty = (): PartyInfo => ({ company: '', contact: '', address: '', city: '', state: '', phone: '', email: '' });
const n   = (v: string) => parseFloat(v) || 0;
type Currency = 'USD' | 'CAD';
const fmtCur = (v: number, cur: Currency) =>
  (cur === 'CAD' ? 'CA$' : 'US$') + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function computeQuote(q: QuoteTab, route: RouteInfo | null) {
  const mi       = route?.distanceMiles ?? 0;
  const stops    = route?.numStops ?? 0;
  const base     = mi * n(q.ratePerMile);
  const fuel     = n(q.fuelSurcharge);
  const stopsAmt = stops * n(q.ratePerStop);
  const acc      = q.accessorials.reduce((s, a) => s + n(a.amount), 0);
  const sub      = base + fuel + stopsAmt + acc;
  const ftl      = sub * (n(q.ftlLtl) / 100);
  const margin   = ftl * (n(q.margin) / 100);
  const total    = ftl + margin;
  return { base, fuel, stopsAmt, acc, sub, ftl, margin, total, mi, stops };
}

async function geocode(address: string): Promise<[number, number]> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1&countrycodes=ca,us`;
  const res  = await fetch(url, { headers: { 'Accept-Language': 'en', 'User-Agent': 'IntegraAI-FreightCalc/1.0' } });
  const data = await res.json();
  if (!data?.length) throw new Error(`Could not find "${address}". Try a more specific address.`);
  return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
}

async function fetchRoute(waypoints: [number, number][]): Promise<RouteInfo> {
  const coords = waypoints.map(([lat, lon]) => `${lon},${lat}`).join(';');
  const res    = await fetch(
    `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`
  );
  const data   = await res.json();
  if (data.code !== 'Ok' || !data.routes?.length) throw new Error('No driving route found between these locations.');
  const r = data.routes[0];
  return {
    distanceMiles: (r.distance / 1000) * 0.621371,
    distanceKm:    r.distance / 1000,
    durationMin:   r.duration / 60,
    numStops:      waypoints.length - 2,
    polyline:      r.geometry.coordinates.map(([lon, lat]: [number, number]) => [lat, lon] as [number, number]),
    markers:       waypoints,
  };
}

/* ── Map fit bounds helper ───────────────────────────────────────────── */
function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) map.fitBounds(L.latLngBounds(positions), { padding: [40, 40] });
  }, [map, positions]);
  return null;
}

/* ── PDF export ──────────────────────────────────────────────────────── */
function exportPDF(
  q: QuoteTab, route: RouteInfo | null, c: ReturnType<typeof computeQuote>,
  customer: PartyInfo, consignor: PartyInfo, consignee: PartyInfo,
  pickup: string, destination: string,
  companyName: string, userFullName: string,
  currency: Currency,
) {
  const esc  = (s: string) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const f    = (v: number) => fmtCur(v, currency);
  const cur  = currency;
  const headerName = companyName || userFullName || '';
  const logoB64 = localStorage.getItem('integra_company_logo') || '';

  const partyBlock = (title: string, p: PartyInfo) => `
    <div class="party">
      <h4>${esc(title)}</h4>
      ${p.company ? `<p><strong>${esc(p.company)}</strong></p>` : ''}
      ${p.contact ? `<p>${esc(p.contact)}</p>` : ''}
      ${p.address ? `<p>${esc(p.address)}</p>` : ''}
      ${p.city    ? `<p>${esc(p.city)}${p.state ? ', ' + esc(p.state) : ''}</p>` : ''}
      ${p.phone   ? `<p>&#x1F4DE; ${esc(p.phone)}</p>` : ''}
      ${p.email   ? `<p>&#x2709; ${esc(p.email)}</p>` : ''}
    </div>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>${esc(q.label)} — Freight Quote</title>
  <style>
    *  { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; color: #111; font-size: 13px; margin: 0; padding: 0; }

    /* ── Letterhead ── */
    .letterhead { display:flex; align-items:center; justify-content:space-between;
                  border-bottom: 3px solid #dc2626; padding-bottom: 14px; margin-bottom: 20px; }
    .lh-company { font-size: 22px; font-weight: 900; color: #dc2626; letter-spacing: -.3px; }
    .lh-tag     { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: .1em; margin-top: 2px; }
    .lh-right   { text-align: right; font-size: 11px; color: #999; }
    .quote-title { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 16px; }

    /* ── Parties ── */
    h4 { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #888; margin: 0 0 5px; }
    .parties { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px;
               border: 1px solid #eee; border-radius: 6px; padding: 14px; margin-bottom: 18px; }
    .party p { margin: 2px 0; font-size: 12px; }

    /* ── Route bar ── */
    .route-bar { background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px;
                 padding: 10px 14px; margin-bottom: 16px; display: flex; gap: 28px; }
    .route-bar span { font-size: 11px; color: #777; }
    .route-bar strong { color: #dc2626; font-size: 14px; display: block; }

    /* ── Table ── */
    table  { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    th     { background: #f7f7f7; padding: 7px 11px; text-align: left; font-size: 11px;
             text-transform: uppercase; color: #666; border-bottom: 2px solid #eee; }
    td     { padding: 8px 11px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
    .total-row td { font-weight: 700; font-size: 17px; color: #dc2626;
                    border-top: 2px solid #dc2626; padding-top: 11px; border-bottom: none; }
    .disclaimer { margin-top: 18px; font-size: 10px; color: #bbb;
                  border-top: 1px solid #eee; padding-top: 9px; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body>

  <!-- Letterhead -->
  <div class="letterhead">
    <div style="display:flex;align-items:center;gap:12px">
      ${logoB64 ? `<img src="${logoB64}" alt="Logo" style="height:48px;width:auto;object-fit:contain" />` : ''}
      <div>
        ${headerName ? `<div class="lh-company">${esc(headerName)}</div>` : ''}
        <div class="lh-tag">Integrated Supply Chain Solutions</div>
      </div>
    </div>
    <div class="lh-right">
      <div>Freight Quote</div>
      <div>${new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })}</div>
    </div>
  </div>

  <div class="quote-title">${esc(q.label)}</div>

  <!-- Parties -->
  <div class="parties">
    ${partyBlock('Customer', customer)}
    ${partyBlock('Consignor / Shipper', consignor)}
    ${partyBlock('Consignee / Receiver', consignee)}
  </div>

  <!-- Route bar -->
  ${route ? `
  <div class="route-bar">
    <div><span>Route</span><strong>${esc(pickup||'—')} &rarr; ${esc(destination||'—')}</strong></div>
    <div><span>Distance</span><strong>${c.mi.toFixed(1)} mi (${route.distanceKm.toFixed(1)} km)</strong></div>
    <div><span>Est. Drive Time</span><strong>~${Math.floor(route.durationMin/60)}h ${Math.round(route.durationMin%60)}m</strong></div>
  </div>` : ''}

  <!-- Cost table — margin baked into total, not shown as line item -->
  <table>
    <tr><th>Description</th><th>Details</th><th style="text-align:right">Amount (${cur})</th></tr>
    <tr><td>Linehaul Rate</td><td>${c.mi.toFixed(1)} mi &times; ${esc(f(n(q.ratePerMile)))}/mi</td><td style="text-align:right">${f(c.base)}</td></tr>
    <tr><td>Fuel Surcharge</td><td>Flat rate</td><td style="text-align:right">${f(c.fuel)}</td></tr>
    <tr><td>Stop Charges</td><td>${c.stops} stop(s) &times; ${f(n(q.ratePerStop))}</td><td style="text-align:right">${f(c.stopsAmt)}</td></tr>
    ${q.accessorials.map(a => `<tr><td>${esc(a.name || 'Accessorial')}</td><td>—</td><td style="text-align:right">${f(n(a.amount))}</td></tr>`).join('')}
    <tr class="total-row"><td colspan="2">TOTAL QUOTE</td><td style="text-align:right">${f(c.total)} ${cur}</td></tr>
  </table>

  <p class="disclaimer">This is an estimate only. Rates include applicable service fees. Actual charges may vary based on final weight, distance, and accessorial requirements.${headerName ? ` &copy; ${new Date().getFullYear()} ${esc(headerName)}.` : ''}</p>
  </body></html>`;

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;width:190mm;background:#fff;';
  wrapper.innerHTML = html;
  document.body.appendChild(wrapper);

  html2pdf()
    .set({
      margin:      12,
      filename:    `${(q.label || 'Freight_Quote').replace(/[^a-zA-Z0-9_-]/g, '_')}_Quote.pdf`,
      html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' },
      jsPDF:       { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .from(wrapper)
    .save()
    .finally(() => document.body.removeChild(wrapper));
}

/* ── Component ───────────────────────────────────────────────────────── */
const FreightCalculatorPage: React.FC = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  /* Profile (for PDF letterhead) */
  const [companyName, setCompanyName] = useState('');
  useEffect(() => {
    if (!token) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${BACKEND_URL}/api/user/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.company) setCompanyName(d.company); })
      .catch(() => {});
  }, [token]);

  /* Quotes */
  const [quotes, setQuotes]     = useState<QuoteTab[]>([blankQ()]);
  const [activeId, setActiveId] = useState(() => quotes[0].id);

  /* Parties */
  const [customer,  setCustomer]  = useState<PartyInfo>(blankParty());
  const [consignor, setConsignor] = useState<PartyInfo>(blankParty());
  const [consignee, setConsignee] = useState<PartyInfo>(blankParty());

  /* Route */
  const [pickup, setPickup]             = useState('');
  const [extraStops, setExtraStops]     = useState<string[]>([]);
  const [destination, setDestination]   = useState('');
  const [route, setRoute]               = useState<RouteInfo | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError]     = useState('');

  /* Currency */
  const [currency, setCurrency] = useState<Currency>('USD');
  const fmt = (v: number) => fmtCur(v, currency);

  /* Map */
  const [mapStyle, setMapStyle] = useState<'osm' | 'sat'>('osm');

  /* Unit converter */
  const [convType, setConvType] = useState<'weight' | 'distance'>('weight');
  const [convA, setConvA]       = useState('');
  const [convB, setConvB]       = useState('');

  /* History save */
  const [saved, setSaved]         = useState(false);
  const [saveError, setSaveError] = useState('');

  const activeQuote = quotes.find(q => q.id === activeId) ?? quotes[0];
  const calc        = computeQuote(activeQuote, route);

  /* Shared input styles — theme-aware */
  const inp = isDark
    ? 'w-full px-2 py-1.5 text-sm border border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-red-500 bg-dark-300 text-gray-100 placeholder-gray-500'
    : 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500 bg-white text-gray-900 placeholder-gray-400';
  const lbl = isDark ? 'block text-xs font-semibold text-gray-400 mb-1' : 'block text-xs font-semibold text-gray-600 mb-1';

  const prefixWrap = isDark
    ? 'flex items-center border border-gray-600 rounded overflow-hidden bg-dark-300 focus-within:ring-1 focus-within:ring-red-500'
    : 'flex items-center border border-gray-300 rounded overflow-hidden bg-white focus-within:ring-1 focus-within:ring-red-500';
  const prefixSpan = isDark
    ? 'px-2 text-xs text-gray-500 border-r border-gray-600 bg-dark-400'
    : 'px-2 text-xs text-gray-400 border-r border-gray-200 bg-gray-50';
  const prefixInput = isDark
    ? 'flex-1 px-2 py-1.5 text-sm focus:outline-none min-w-0 bg-dark-300 text-gray-100'
    : 'flex-1 px-2 py-1.5 text-sm focus:outline-none min-w-0 bg-white text-gray-900';

  const prefixInp = (key: keyof QuoteTab, placeholder = '0.00') => (
    <div className={prefixWrap}>
      <span className={prefixSpan}>$</span>
      <input
        type="number" min="0" step="0.01"
        value={(activeQuote as any)[key]}
        onChange={e => updateQuote({ [key]: e.target.value } as any)}
        placeholder={placeholder}
        className={prefixInput}
      />
    </div>
  );

  /* Quote helpers */
  const updateQuote = useCallback((patch: Partial<QuoteTab>) => {
    setQuotes(qs => qs.map(q => q.id === activeId ? { ...q, ...patch } : q));
  }, [activeId]);

  const addQuote = () => {
    const q = blankQ();
    setQuotes(qs => [...qs, q]);
    setActiveId(q.id);
  };
  const removeQuote = (id: string) => {
    setQuotes(qs => {
      const next = qs.filter(q => q.id !== id);
      if (!next.length) return qs;
      if (id === activeId) setActiveId(next[next.length - 1].id);
      return next;
    });
  };

  /* Accessorials */
  const addAcc    = () => updateQuote({ accessorials: [...activeQuote.accessorials, { id: newId(), amount: '', name: '' }] });
  const removeAcc = (id: string) => updateQuote({ accessorials: activeQuote.accessorials.filter(a => a.id !== id) });
  const updateAcc = (id: string, patch: Partial<AccessorialRow>) =>
    updateQuote({ accessorials: activeQuote.accessorials.map(a => a.id === id ? { ...a, ...patch } : a) });

  /* Route calculation */
  const calculateRoute = async () => {
    const addrs = [pickup, ...extraStops.filter(Boolean), destination].filter(Boolean);
    if (addrs.length < 2) { setRouteError('Enter at least a pickup and destination.'); return; }
    setRouteLoading(true);
    setRouteError('');
    try {
      const coords = await Promise.all(addrs.map(geocode));
      setRoute(await fetchRoute(coords));
    } catch (e: any) {
      setRouteError(e.message || 'Route calculation failed.');
    } finally {
      setRouteLoading(false);
    }
  };

  /* Unit converter */
  const handleConvA = (v: string) => {
    setConvA(v);
    const x = parseFloat(v);
    if (isNaN(x)) { setConvB(''); return; }
    setConvB(convType === 'weight' ? (x * 0.453592).toFixed(3) : (x * 1.60934).toFixed(3));
  };
  const handleConvB = (v: string) => {
    setConvB(v);
    const x = parseFloat(v);
    if (isNaN(x)) { setConvA(''); return; }
    setConvA(convType === 'weight' ? (x * 2.20462).toFixed(3) : (x * 0.621371).toFixed(3));
  };
  useEffect(() => { setConvA(''); setConvB(''); }, [convType]);

  /* Save to history */
  const saveToHistory = async () => {
    if (!token) return;
    setSaveError('');
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/history/freight-quote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:          `${activeQuote.label}${pickup && destination ? ` — ${pickup} to ${destination}` : ''}`,
          customer_name:  customer.company || customer.contact || undefined,
          consignor:      consignor.company || undefined,
          consignee:      consignee.company || undefined,
          origin:         pickup || undefined,
          destination:    destination || undefined,
          distance_miles: route ? parseFloat(route.distanceMiles.toFixed(2)) : undefined,
          distance_km:    route ? parseFloat(route.distanceKm.toFixed(2)) : undefined,
          rate_per_mile:  n(activeQuote.ratePerMile) || undefined,
          fuel_surcharge: n(activeQuote.fuelSurcharge) || undefined,
          rate_per_stop:  n(activeQuote.ratePerStop) || undefined,
          ftl_ltl_pct:    n(activeQuote.ftlLtl),
          margin_pct:     n(activeQuote.margin),
          accessorials:   activeQuote.accessorials.filter(a => a.amount || a.name),
          total_quote:    parseFloat(calc.total.toFixed(2)),
          currency:       currency,
        }),
      });
      if (res.status === 402) { setSaveError('upgrade'); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(parseApiError(err));
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaveError('Network error. Please try again.');
    }
  };

  const panelBorder  = isDark ? 'border-gray-700' : 'border-gray-200';
  const panelBg      = isDark ? 'bg-dark-200'     : 'bg-white';
  const sectionTitle = isDark ? 'text-gray-200'   : 'text-gray-800';
  const subtleText   = isDark ? 'text-gray-500'   : 'text-gray-400';

  const partyFields = (label: string, val: PartyInfo, set: React.Dispatch<React.SetStateAction<PartyInfo>>, Icon: React.ElementType) => (
    <div>
      <div className={`flex items-center gap-1.5 text-xs font-bold mb-3 pb-1 border-b ${isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-100'}`}>
        <Icon className="w-3.5 h-3.5 text-red-500" /> {label}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { f: 'company',  ph: 'Company name',   full: true  },
          { f: 'contact',  ph: 'Contact person',  full: false },
          { f: 'phone',    ph: 'Phone',           full: false },
          { f: 'email',    ph: 'Email',           full: false },
          { f: 'address',  ph: 'Street address',  full: true  },
          { f: 'city',     ph: 'City',            full: false },
          { f: 'state',    ph: 'State/Province',  full: false },
        ].map(({ f, ph, full }) => (
          <input
            key={f}
            type={f === 'email' ? 'email' : 'text'}
            placeholder={ph}
            value={(val as any)[f]}
            onChange={e => set(v => ({ ...v, [f]: e.target.value }))}
            className={`${inp} ${full ? 'col-span-2' : ''}`}
          />
        ))}
      </div>
    </div>
  );

  const userFullName = user?.full_name || user?.name || '';

  return (
    <div className={`min-h-screen pt-20 sm:pt-28 pb-16 px-4 ${isDark ? 'bg-dark-300' : 'bg-gray-100'}`}>
      <div className="max-w-6xl mx-auto">
        <BackToTools />

        {/* ── Tab bar ──────────────────────────────────────────────── */}
        <div className={`flex items-center justify-between border border-b-0 rounded-t-xl px-2 h-10 gap-1 mt-4 ${panelBg} ${panelBorder}`}>
          <div className="flex items-center gap-1 overflow-x-auto">
            {quotes.map(q => (
              <button
                key={q.id}
                onClick={() => setActiveId(q.id)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                  q.id === activeId
                    ? 'bg-red-600 text-white'
                    : isDark ? 'bg-dark-400 text-gray-300 hover:bg-dark-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className="text-xs">📄</span>
                {q.label}
                {quotes.length > 1 && (
                  <span role="button" onClick={e => { e.stopPropagation(); removeQuote(q.id); }} className="ml-1 opacity-70 hover:opacity-100">
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={addQuote}
              className={`ml-1 flex items-center gap-1 px-3 py-1 text-sm rounded border border-dashed transition-colors whitespace-nowrap ${
                isDark ? 'text-gray-400 hover:bg-dark-400 border-gray-600' : 'text-gray-600 hover:bg-gray-100 border-gray-300'
              }`}
            >
              <Plus className="w-3.5 h-3.5" /> New Quote
            </button>
          </div>

          {/* Currency toggle */}
          <div className={`flex shrink-0 ml-2 overflow-hidden rounded border text-xs font-bold ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
            {(['USD', 'CAD'] as Currency[]).map(cur => (
              <button
                key={cur}
                onClick={() => setCurrency(cur)}
                className={`px-2.5 py-1 transition-colors ${
                  currency === cur
                    ? 'bg-red-600 text-white'
                    : isDark ? 'bg-dark-400 text-gray-400 hover:bg-dark-500' : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                {cur}
              </button>
            ))}
          </div>
        </div>

        {/* ── Main: left panel + map ───────────────────────────────── */}
        <div className={`flex border ${panelBg} ${panelBorder}`}>

          {/* Left panel — no scroll */}
          <div className={`w-64 shrink-0 flex flex-col border-r ${panelBorder}`}>
            <div className="p-3 space-y-3">
              <div className={`flex items-center gap-1.5 text-sm font-bold border-b pb-2 ${sectionTitle} ${panelBorder}`}>
                <DollarSign className="w-4 h-4 text-red-600" /> Quote Calculator
              </div>

              {/* Editable quote name */}
              <div>
                <label className={lbl}>Quote Name</label>
                <input
                  type="text"
                  value={activeQuote.label}
                  onChange={e => updateQuote({ label: e.target.value })}
                  placeholder="e.g. Quote for Acme Corp"
                  className={`${inp} font-medium`}
                />
              </div>

              {([
                ['Rate per Mile',   'ratePerMile'],
                ['Fuel Surcharge',  'fuelSurcharge'],
                ['Rate per Stop',   'ratePerStop'],
                ['Rate per Lbs',    'ratePerLbs'],
              ] as [string, keyof QuoteTab][]).map(([label, key]) => (
                <div key={key}>
                  <label className={lbl}>{label}</label>
                  {prefixInp(key)}
                </div>
              ))}

              {/* FTL/LTL */}
              <div>
                <label className={lbl}>FTL/LTL (%)</label>
                <div className={`flex items-center border rounded overflow-hidden focus-within:ring-1 focus-within:ring-red-500 ${isDark ? 'border-gray-600 bg-dark-300' : 'border-gray-300 bg-white'}`}>
                  <input
                    type="number" min="0" max="100"
                    value={activeQuote.ftlLtl}
                    onChange={e => updateQuote({ ftlLtl: e.target.value })}
                    className={`flex-1 px-2 py-1.5 text-sm focus:outline-none ${isDark ? 'bg-dark-300 text-gray-100' : 'bg-white text-gray-900'}`}
                  />
                  <span className={prefixSpan}>%</span>
                </div>
                <p className={`text-[10px] mt-1 ${subtleText}`}>0% = LTL &nbsp;·&nbsp; 100% = FTL</p>
              </div>

              {/* Accessorials */}
              <div>
                <label className={lbl}>Accessorial Charges</label>
                <div className="space-y-1.5">
                  {activeQuote.accessorials.map(a => (
                    <div key={a.id} className="flex gap-1">
                      <div className={`flex items-center border rounded overflow-hidden w-20 shrink-0 ${isDark ? 'border-gray-600 bg-dark-300' : 'border-gray-300 bg-white'}`}>
                        <span className={`px-1 text-xs border-r ${isDark ? 'text-gray-500 border-gray-600 bg-dark-400' : 'text-gray-400 border-gray-200 bg-gray-50'}`}>$</span>
                        <input type="number" min="0" value={a.amount}
                          onChange={e => updateAcc(a.id, { amount: e.target.value })}
                          placeholder="0.00" className={`flex-1 px-1 py-1 text-xs focus:outline-none w-0 ${isDark ? 'bg-dark-300 text-gray-100' : 'bg-white text-gray-900'}`} />
                      </div>
                      <input type="text" value={a.name}
                        onChange={e => updateAcc(a.id, { name: e.target.value })}
                        placeholder="Name"
                        className={`flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-red-500 ${isDark ? 'border-gray-600 bg-dark-300 text-gray-100 placeholder-gray-500' : 'border-gray-300 bg-white text-gray-900'}`} />
                      <button onClick={() => removeAcc(a.id)} className={`${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button onClick={addAcc} className="text-xs text-red-600 hover:text-red-500 font-medium flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add Accessorial
                  </button>
                </div>
              </div>

              {/* Margin */}
              <div>
                <label className={lbl}>Margin (%)</label>
                <div className={`flex items-center border rounded overflow-hidden focus-within:ring-1 focus-within:ring-red-500 ${isDark ? 'border-gray-600 bg-dark-300' : 'border-gray-300 bg-white'}`}>
                  <input type="number" min="0" value={activeQuote.margin}
                    onChange={e => updateQuote({ margin: e.target.value })}
                    placeholder="0" className={`flex-1 px-2 py-1.5 text-sm focus:outline-none ${isDark ? 'bg-dark-300 text-gray-100' : 'bg-white text-gray-900'}`} />
                  <span className={prefixSpan}>%</span>
                </div>
              </div>
            </div>

            {/* Total Quote */}
            <div className="mt-auto bg-gradient-to-b from-red-600 to-red-800 text-white p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase tracking-wide opacity-80">Total Quote</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold opacity-60">{currency}</span>
                  <button onClick={() => navigator.clipboard?.writeText(calc.total.toFixed(2))} title="Copy total" className="opacity-60 hover:opacity-100">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="text-3xl font-black mb-3">{fmt(calc.total)}</div>
              <div className="text-[11px] space-y-0.5 opacity-90 border-t border-white/20 pt-2">
                <div className="flex justify-between">
                  <span>
                    Linehaul ({calc.mi.toFixed(0)} mi × {fmt(n(activeQuote.ratePerMile))}/mi
                    {n(activeQuote.margin) > 0 ? ` +${n(activeQuote.margin)}%` : ''})
                  </span>
                  <span>{fmt(calc.base)}</span>
                </div>
                <div className="flex justify-between"><span>+ Fuel Surcharge</span><span>{fmt(calc.fuel)}</span></div>
                <div className="flex justify-between"><span>+ Stops ({calc.stops} × {fmt(n(activeQuote.ratePerStop))})</span><span>{fmt(calc.stopsAmt)}</span></div>
                <div className="flex justify-between"><span>+ Accessorials</span><span>{fmt(calc.acc)}</span></div>
                <div className="flex justify-between font-bold border-t border-white/20 pt-1 mt-1"><span>Subtotal</span><span>{fmt(calc.sub)}</span></div>
                <div className="flex justify-between"><span>× {n(activeQuote.ftlLtl)}% (FTL/LTL adj.)</span><span>{fmt(calc.ftl)}</span></div>
                {n(activeQuote.margin) > 0 && (
                  <div className="flex justify-between opacity-70"><span>+ Margin ({n(activeQuote.margin)}%)</span><span>{fmt(calc.margin)}</span></div>
                )}
              </div>

              <div className="mt-3 flex flex-col gap-2">
                <button
                  onClick={() => {
                    exportPDF(activeQuote, route, calc, customer, consignor, consignee, pickup, destination, companyName, userFullName, currency);
                    if (isPaidUser(user)) saveToHistory();
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-white text-red-700 font-bold text-xs py-2 rounded hover:bg-red-50 transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" />
                  {saved ? 'Exported & Saved!' : 'Export PDF Quote'}
                </button>
                {saved && (
                  <p className="text-[10px] text-green-300 text-center flex items-center justify-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Saved to your history
                  </p>
                )}
                {!isPaidUser(user) && (
                  <p className="text-[10px] text-white/50 text-center flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" /> Upgrade to Pro to save history
                  </p>
                )}
                {saveError === 'upgrade' ? (
                  <p className="text-[10px] text-red-200">Upgrade to Pro to save quotes to history.</p>
                ) : saveError ? (
                  <p className="text-[10px] text-red-200">{saveError}</p>
                ) : null}
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="flex-1 relative" style={{ minHeight: '620px' }}>
            {/* Map/Satellite toggle */}
            <div className={`absolute top-3 right-3 z-[1000] flex overflow-hidden rounded border shadow-sm ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              {(['osm', 'sat'] as const).map(s => (
                <button key={s} onClick={() => setMapStyle(s)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    mapStyle === s
                      ? 'bg-red-600 text-white'
                      : isDark ? 'bg-dark-300 text-gray-300 hover:bg-dark-400' : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}>
                  {s === 'osm' ? 'Map' : 'Satellite'}
                </button>
              ))}
            </div>

            <MapContainer center={[48, -96]} zoom={3} style={{ height: '100%', width: '100%', minHeight: '620px' }} zoomControl>
              <TileLayer
                key={mapStyle}
                url={mapStyle === 'osm'
                  ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                  : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'}
                attribution={mapStyle === 'osm'
                  ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  : '&copy; Esri &mdash; Esri, i-cubed, USDA, USGS'}
              />
              {route && route.polyline.length > 1 && (
                <>
                  <Polyline positions={route.polyline} pathOptions={{ color: '#dc2626', weight: 4, opacity: 0.9 }} />
                  {route.markers.map((pos, i) => <Marker key={i} position={pos} />)}
                  <FitBounds positions={route.polyline} />
                </>
              )}
              {!route && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 400 }}>
                  <div className="text-center text-gray-500 bg-white/80 rounded-xl px-6 py-4 shadow-md">
                    <div className="text-4xl mb-2">🗺️</div>
                    <p className="font-semibold text-sm">No route calculated</p>
                    <p className="text-xs mt-1 text-gray-400">Enter locations below to view route</p>
                  </div>
                </div>
              )}
            </MapContainer>
          </div>
        </div>

        {/* ── Customer / Consignor / Consignee ─────────────────────── */}
        <div className={`border border-t-0 p-5 ${panelBg} ${panelBorder}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {partyFields('Customer', customer, setCustomer, User)}
            {partyFields('Consignor / Shipper', consignor, setConsignor, Building2)}
            {partyFields('Consignee / Receiver', consignee, setConsignee, Building2)}
          </div>
        </div>

        {/* ── Bottom: unit converter + route calculator ────────────── */}
        <div className={`flex border border-t-0 rounded-b-xl overflow-hidden ${panelBg} ${panelBorder}`}>

          {/* Unit Converter */}
          <div className={`w-1/2 p-4 border-r ${panelBorder}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`flex items-center gap-2 text-sm font-bold ${sectionTitle}`}>
                <ArrowLeftRight className="w-4 h-4 text-red-600" /> Unit Converter
              </div>
              <select value={convType} onChange={e => setConvType(e.target.value as any)}
                className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-red-500 ${
                  isDark ? 'border-gray-600 bg-dark-300 text-gray-200' : 'border-gray-300 bg-white text-gray-800'
                }`}>
                <option value="weight">Weight</option>
                <option value="distance">Distance</option>
              </select>
            </div>
            <div className="grid grid-cols-[1fr,auto,1fr] gap-2 items-center mb-2">
              <div>
                <label className={`text-xs block mb-1 ${subtleText}`}>{convType === 'weight' ? 'Pounds (lbs)' : 'Miles (mi)'}</label>
                <input type="number" value={convA} onChange={e => handleConvA(e.target.value)}
                  placeholder={convType === 'weight' ? 'Enter lbs' : 'Enter miles'} className={inp} />
              </div>
              <button onClick={() => { const t = convA; setConvA(convB); setConvB(t); }} className={`mt-5 ${isDark ? 'text-gray-500 hover:text-red-500' : 'text-gray-400 hover:text-red-600'}`}>
                <ArrowLeftRight className="w-4 h-4" />
              </button>
              <div>
                <label className={`text-xs block mb-1 ${subtleText}`}>{convType === 'weight' ? 'Kilograms (kg)' : 'Kilometers (km)'}</label>
                <input type="number" value={convB} onChange={e => handleConvB(e.target.value)}
                  placeholder={convType === 'weight' ? 'Enter kg' : 'Enter km'} className={inp} />
              </div>
            </div>
            <p className={`text-[11px] ${subtleText}`}>
              {convType === 'weight' ? '1 lb = 0.454 kg  ·  1 kg = 2.205 lbs' : '1 mi = 1.609 km  ·  1 km = 0.621 mi'}
            </p>
          </div>

          {/* Route Calculator */}
          <div className="w-1/2 p-4">
            <div className={`flex items-center gap-2 text-sm font-bold mb-3 ${sectionTitle}`}>
              <Navigation className="w-4 h-4 text-red-600" /> Route Calculator
            </div>
            <div className="space-y-2 mb-3">
              <div className="relative">
                <MapPin className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-green-500" />
                <input type="text" value={pickup} onChange={e => setPickup(e.target.value)}
                  placeholder="Pickup location (city, state/province)"
                  className={`${inp} pl-7`} />
              </div>
              {extraStops.map((stop, i) => (
                <div key={i} className="flex gap-1">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-yellow-500" />
                    <input type="text" value={stop}
                      onChange={e => setExtraStops(s => s.map((v, j) => j === i ? e.target.value : v))}
                      placeholder="Stop (optional)" className={`${inp} pl-7`} />
                  </div>
                  <button onClick={() => setExtraStops(s => s.filter((_, j) => j !== i))} className={`${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <MapPin className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-red-500" />
                  <input type="text" value={destination} onChange={e => setDestination(e.target.value)}
                    placeholder="Destination (city, state/province)"
                    className={`${inp} pl-7`} />
                </div>
                <button onClick={() => setExtraStops(s => [...s, ''])} title="Add stop"
                  className="px-2.5 bg-red-600 text-white rounded hover:bg-red-700">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            {routeError && <p className="text-xs text-red-500 mb-2">{routeError}</p>}
            {route && (
              <p className="text-xs text-green-500 mb-2 font-medium">
                ✓ {route.distanceMiles.toFixed(1)} mi · {route.distanceKm.toFixed(1)} km · ~{Math.floor(route.durationMin / 60)}h {Math.round(route.durationMin % 60)}m drive time
              </p>
            )}
            <button onClick={calculateRoute} disabled={routeLoading}
              className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 rounded transition-colors disabled:opacity-60">
              {routeLoading
                ? <><RotateCcw className="w-4 h-4 animate-spin" /> Calculating...</>
                : <><Navigation className="w-4 h-4" /> Calculate Route</>}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FreightCalculatorPage;
